import { SetupContext, computed, inject, reactive, ref } from "vue";
import { UiStepsInputProps, UiStepsInputEmits } from "../index";
import { UiFormEmitterKey } from "@various/constants";
import { utility } from "@various/utils";

export const useComposable = (define: UiStepsInputProps, emit: SetupContext<typeof UiStepsInputEmits>["emit"]) => {
    //* 初始化mitt
    const emitter = inject(UiFormEmitterKey, undefined);

    //* 响应式属性
    const refs = {
        currentPage: ref<string>(""),
    };

    //* 函数列表
    const methods = {
        //* Input失去焦点事件
        blur: (ev: FocusEvent | Event) => {
            //* 触发blur回调
            emit("blur", ev);
            //* 触发表单blur相关校验
            emitter?.emit(define.name || "", "blur");
            //* 失焦重置当前模块
            refs.currentPage.value = "";
        },

        //* Input获取焦点事件
        focus: (ev: FocusEvent | Event) => {
            //* 触发focus回调
            emit("focus", ev);
        },

        //* Input触发input事件
        input: (ev: InputEvent) => {
            //* 触发v-model变更和input回调
            emit("input", ev);
        },

        //* Input触发change事件
        change: (ev: Event) => {
            //* 当输入完整时, 触发change回调
            if (!define.modelValue?.start?.toString() || !define.modelValue?.end?.toString()) return;
            else {
                //* 触发change回调
                emit("change", ev);
                //* 触发表单change相关校验
                emitter?.emit(define.name || "", "change");
            }
        },
    };

    //* 计算属性
    const computeds = {
        //* 样式列表
        style: computed(() => {
            return {
                width: (define.width - (define.spacing * 2 + 12)) / 2 + "px",
            };
        }),

        //* 标签类名
        className: computed(() => {
            //* 初始化输出列表
            const result: string[] = [];
            //* 判断是否是禁用或只读状态
            if (define.disabled) result.push("ui-disabled-status");
            else if (define.readonly) result.push("ui-readonly-status");
            //* 判断是否需要添加size类名
            if (define.size != "default") result.push(`ui-${define.size}`);

            return result.join(" ");
        }),
    };

    //* 动态属性
    const binds = reactive({
        //* 容器属性
        container: computed(() => {
            return {
                class: computeds.className.value,
                style: {
                    width: `${define.width || 0}px`,
                },
            };
        }),

        //* 增量输入框属性
        number: computed(() => {
            return {
                type: "number",
                disabled: define.disabled,
                readonly: define.readonly,
                placeholder: "0",
                style: {
                    width: `${define.widthNumber || 0}px`,
                },
            };
        }),

        //* 主输入框属性
        input: computed(() => {
            const data = { type: "number", disabled: define.disabled, readonly: define.readonly };
            return {
                end: Object.assign(utility.cloneDeep(data), { placeholder: define.placeholder?.end || "" }),
                start: Object.assign(utility.cloneDeep(data), { placeholder: define.placeholder?.start || "" }),
            };
        }),

        //* 分割线属性
        rect: computed(() => {
            return {
                style: {
                    width: `${define.spacing * 2 + 12}px`,
                },
            };
        }),
    });

    //* 响应事件
    const ons = {
        start: {
            blur: methods.blur,
            input: methods.input,
            change: methods.change,
            focus: (ev: FocusEvent | Event) => {
                refs.currentPage.value = "start";
                methods.focus(ev);
            },
        },

        end: {
            blur: methods.blur,
            input: methods.input,
            change: methods.change,
            focus: (ev: FocusEvent | Event) => {
                refs.currentPage.value = "end";
                methods.focus(ev);
            },
        },
    };

    return { ons, refs, binds, methods, computeds };
};
